import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RefreshCw,
  LayoutDashboard,
  Building2,
  CircleUser as UserCircle,
  Package,
  Wallet,
  Shield,
  TrendingUp as PiggyBank,
  BarChart3,
  Menu,
  X,
} from 'lucide-react';
import { Dashboard, type KpiType } from '../components/Dashboard';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { TableView } from '../components/TableView';
import { CampaignGridView } from '../components/CampaignGridView';
import { MultiInfluencerTableView } from '../components/MultiInfluencerTableView';
import type { ViewMode } from '../components/ViewToggle';
import { CampaignForm } from '../components/CampaignForm';
import { CampaignFormMultiInfluencer } from '../components/CampaignFormMultiInfluencer';
import { CampaignFormSponsorship } from '../components/CampaignFormSponsorship';
import { CampaignModeSelector } from '../components/CampaignModeSelector';
import { FilterToolbar } from '../components/FilterToolbar';
import { FilterChips } from '../components/FilterChips';
import { ClientsManager } from '../components/ClientsManager';
import { InfluencersManager } from '../components/InfluencersManager';
import { ServicesManager } from '../components/ServicesManager';
import { Commissions } from '../components/Commissions';
import { Statistics } from '../components/Statistics';
import { AdminPanel } from '../components/AdminPanel';
import { ProjectoForm } from '../components/projecto/ProjectoForm';
import { ProjectoList } from '../components/projecto/ProjectoList';
import { KpiModal } from '../components/KpiModal';
import { ProfileMenu } from '../components/ProfileMenu';
import { HubSwitcher } from '../components/HubSwitcher';
import { PaymentsPage } from '../components/PaymentsPage';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { campaignService, CAMPAIGN_DELETE_DENIED } from '../services/campaignService';
import { projectoCampaignService } from '../services/projectoService';
import { userService } from '../services/userService';
import { supabase } from '../lib/supabase';
import { PROJECTO_TO_CAMPAIGN_STATUS, type ProjectoCampaign } from '../types/projecto';
import { useCampaignsRealtime } from '../hooks/useCampaignRealtime';
import { useUserPreferences } from '../hooks/useUserPreferences';
import type { CampaignWithComputed, CampaignFilters, KpiStats, CampaignStatus } from '../types/campaign';
import type { UserRole } from '../types/user';
import type { CampaignMode } from '../types/multiInfluencer';
import { isPaymentReady, hasOverdueInvoices } from '../utils/paymentCalculations';
import { subscribeMissingColumn } from '../lib/pendingSchema';
import { needsClientInvoice } from '../utils/invoicePredicates';
import { log } from '../lib/logger';

type PageView = 'campaigns' | 'clients' | 'influencers' | 'services' | 'payments' | 'commissions' | 'statistics' | 'admin';
type CampaignModeFilter = 'all' | 'brand_action' | 'influencer_marketing' | 'sponsorship' | 'projecto' | 'services';
type ListItem =
  | { kind: 'campaign'; data: CampaignWithComputed }
  | { kind: 'projecto'; data: ProjectoCampaign };

const projectoStatusMap: Record<string, CampaignStatus | undefined> = PROJECTO_TO_CAMPAIGN_STATUS;

function filterByMode(campaigns: CampaignWithComputed[], mode: CampaignModeFilter): CampaignWithComputed[] {
  if (mode === 'brand_action') return campaigns.filter(c => c.campaign_mode === 'brand_action' || !c.campaign_mode);
  if (mode === 'influencer_marketing') return campaigns.filter(c => c.campaign_mode === 'influencer_marketing');
  if (mode === 'sponsorship') return campaigns.filter(c => c.campaign_mode === 'sponsorship');
  if (mode === 'services') return campaigns.filter(c => c.campaign_mode === 'services');
  return campaigns;
}

export default function MainApp() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useModal();
  const { preferences, updateViewMode } = useUserPreferences();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState<CampaignWithComputed[]>([]);
  const [filteredItems, setFilteredItems] = useState<ListItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [loading, setLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [pageView, setPageView] = useState<PageView>('campaigns');
  const [selectedStatuses, setSelectedStatuses] = useState<CampaignStatus[]>([]);
  const [campaignModeFilter, setCampaignModeFilter] =
    useState<CampaignModeFilter>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignWithComputed | null>(null);
  const [selectedCampaignMode, setSelectedCampaignMode] = useState<CampaignMode | null>(null);
  const [filters, setFilters] = useState<CampaignFilters>({});
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [kpiStats, setKpiStats] = useState<KpiStats>({
    campaigns_active: 0,
    campaigns_consultation: 0,
    campaigns_in_progress: 0,
    invoices_not_issued: 0,
    invoices_overdue: 0,
    payments_ready: 0,
    payments_ready_amount: 0,
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiModalTitle, setKpiModalTitle] = useState('');
  const [kpiModalCampaigns, setKpiModalCampaigns] = useState<CampaignWithComputed[]>([]);
  const [kpiModalProjectos, setKpiModalProjectos] = useState<NonNullable<KpiStats['invoice_request_projectos']>>([]);
  const [projectoCampaigns, setProjectoCampaigns] = useState<ProjectoCampaign[]>([]);
  const [editingProjecto, setEditingProjecto] = useState<ProjectoCampaign | null>(null);
  // Opened from a notification about an invoice: the form starts on its invoices
  const [focusInvoices, setFocusInvoices] = useState(false);

  const NAV_ITEMS = [
    { key: 'campaigns' as PageView, icon: LayoutDashboard, label: t('nav.campaigns'), show: true },
    { key: 'clients' as PageView, icon: Building2, label: t('nav.clients'), show: !!(userRole?.can_view_clients || userRole?.can_manage_users) },
    { key: 'influencers' as PageView, icon: UserCircle, label: t('nav.influencers'), show: !!(userRole?.can_view_influencers || userRole?.can_manage_users) },
    { key: 'services' as PageView, icon: Package, label: t('nav.services'), show: true },
    { key: 'payments' as PageView, icon: Wallet, label: t('nav.payments'), show: !!(userRole?.can_view_payments || userRole?.can_manage_users) },
    { key: 'commissions' as PageView, icon: PiggyBank, label: t('nav.commissions'), show: !!(userRole?.can_view_commissions || userRole?.can_manage_users) },
    { key: 'statistics' as PageView, icon: BarChart3, label: t('nav.statistics'), show: !!(userRole?.can_view_statistics || userRole?.can_manage_users) },
    { key: 'admin' as PageView, icon: Shield, label: t('nav.admin'), show: !!userRole?.can_manage_users },
  ];

  const listItems = useMemo((): ListItem[] => [
    ...campaigns.map((c): ListItem => ({ kind: 'campaign', data: c })),
    ...projectoCampaigns.map((p): ListItem => ({ kind: 'projecto', data: p })),
  ], [campaigns, projectoCampaigns]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const [data, projectoData] = await Promise.all([
        campaignService.getAll(),
        projectoCampaignService.getAll(),
      ]);
      setCampaigns(data);
      setProjectoCampaigns(projectoData);
    } catch (error) {
      log.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKpiStats = async (year?: number) => {
    try {
      setKpiLoading(true);
      const stats = await campaignService.calculateKpiStats(year);
      setKpiStats(stats);
    } catch (error) {
      log.error('Error loading KPI stats:', error);
    } finally {
      setKpiLoading(false);
    }
  };



  // A pending database update no longer blocks saving; warn about what was
  // left out so the SQL actually gets applied.
  useEffect(() => subscribeMissingColumn((column) => {
    toast.warning(t('error.missingColumnSaved').replace('{0}', column), 12000);
  }), [toast, t]);

  useEffect(() => {
    log.debug('[NAV-MOUNT] MainApp.tsx mounted');
    const loadUserRole = async () => {
      const role = await userService.getCurrentUserRole();
      log.debug('User role loaded:', role);
      setUserRole(role);

      if (role?.role === 'influencer') {
        log.debug('[MainApp] Influencer detected, redirecting to back office');
        window.location.href = '/creatorshub/portal';
        return;
      }
    };
    loadUserRole();
    loadCampaigns();
    loadKpiStats();
  }, []);

  useEffect(() => {
    loadKpiStats(filters.year);
  }, [filters.year]);

  useCampaignsRealtime(
    (updatedCampaign) => {
      setCampaigns((prev) =>
        prev.map((c) => {
          if (c.id !== updatedCampaign.id) return c;
          const merged: any = { ...c, ...updatedCampaign };
          // Table-backed campaigns (IM/sponsorship) had their invoice arrays
          // hydrated from the invoices table on load; the realtime row only
          // carries the stale JSON snapshot, so keep the hydrated arrays.
          if (c.campaign_mode === 'influencer_marketing' || c.campaign_mode === 'sponsorship') {
            merged.mohit_invoices = (c as any).mohit_invoices;
            merged.influencer_invoices = (c as any).influencer_invoices;
          }
          return merged;
        })
      );
      loadKpiStats(filters.year);
    },
    (newCampaign) => {
      loadCampaigns();
      loadKpiStats(filters.year);
    },
    (deletedCampaignId) => {
      setCampaigns((prev) => prev.filter((c) => c.id !== deletedCampaignId));
      loadKpiStats(filters.year);
    }
  );

  useEffect(() => {
    if (preferences?.campaign_view_mode) {
      setViewMode(preferences.campaign_view_mode);
    }
  }, [preferences?.campaign_view_mode]);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    updateViewMode(mode).catch(err => {
      log.error('Failed to save view mode preference:', err);
    });
  };

  useEffect(() => {
    let filtered: ListItem[];

    if (campaignModeFilter === 'projecto') {
      filtered = listItems.filter(i => i.kind === 'projecto');
      if (filters.search) {
        const q = filters.search.toLowerCase();
        filtered = filtered.filter(i => {
          const p = (i as { kind: 'projecto'; data: ProjectoCampaign }).data;
          return (
            p.brand?.toLowerCase().includes(q) ||
            p.client_name?.toLowerCase().includes(q) ||
            p.name?.toLowerCase().includes(q) ||
            p.work_code?.toLowerCase().includes(q)
          );
        });
      }
    } else {
      const campaignData = listItems
        .filter((i): i is { kind: 'campaign'; data: CampaignWithComputed } => i.kind === 'campaign')
        .map(i => i.data)
        .filter(c => c.campaign_mode !== 'projecto');
      const filteredData = filterByMode(campaignService.filterCampaigns(campaignData, filters), campaignModeFilter);
      filtered = filteredData.map((c): ListItem => ({ kind: 'campaign', data: c }));
    }

    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(i => {
        const status: CampaignStatus | undefined =
          i.kind === 'campaign'
            ? ((i.data.campaign_status || 'Consultation') as CampaignStatus)
            : projectoStatusMap[i.data.status];
        return status !== undefined && selectedStatuses.includes(status);
      });
    }

    setFilteredItems(filtered);
  }, [campaigns, projectoCampaigns, listItems, filters, selectedStatuses, campaignModeFilter]);

  const statusCounts = useMemo(() => {
    const getStatus = (item: ListItem): CampaignStatus | undefined =>
      item.kind === 'campaign'
        ? ((item.data.campaign_status || 'Consultation') as CampaignStatus)
        : projectoStatusMap[item.data.status];

    let modeItems: ListItem[];
    if (campaignModeFilter === 'projecto') {
      modeItems = listItems.filter(i => i.kind === 'projecto');
    } else {
      const campaignData = listItems
        .filter((i): i is { kind: 'campaign'; data: CampaignWithComputed } => i.kind === 'campaign')
        .map(i => i.data);
      modeItems = filterByMode(campaignService.filterCampaigns(campaignData, filters), campaignModeFilter)
        .map((c): ListItem => ({ kind: 'campaign', data: c }));
    }

    return [
      { status: 'Consultation' as CampaignStatus, count: modeItems.filter(i => getStatus(i) === 'Consultation').length, label: t('status.consultation') },
      { status: 'Confirmed' as CampaignStatus, count: modeItems.filter(i => getStatus(i) === 'Confirmed').length, label: t('status.confirmed') },
      { status: 'In Progress' as CampaignStatus, count: modeItems.filter(i => getStatus(i) === 'In Progress').length, label: t('status.inProgress') },
      { status: 'Billing' as CampaignStatus, count: modeItems.filter(i => getStatus(i) === 'Billing').length, label: t('status.billing') },
      { status: 'Completed' as CampaignStatus, count: modeItems.filter(i => getStatus(i) === 'Completed').length, label: t('status.completed') },
    ];
  }, [campaigns, projectoCampaigns, listItems, filters, campaignModeFilter, t]);

  const modeCounts = useMemo(() => {
    const filtered = campaignService.filterCampaigns(campaigns, filters);
    return {
      all: filtered.length,
      brandAction: filterByMode(filtered, 'brand_action').length,
      influencerMarketing: filterByMode(filtered, 'influencer_marketing').length,
      sponsorship: filterByMode(filtered, 'sponsorship').length,
      services: filterByMode(filtered, 'services').length,
      projecto: projectoCampaigns.length,
    };
  }, [campaigns, projectoCampaigns, filters]);

  const handleEdit = (item: ListItem) => {
    if (item.kind === 'projecto') {
      setEditingProjecto(item.data);
      setSelectedCampaignMode('projecto');
      setShowForm(true);
      return;
    }
    const mode = item.data.campaign_mode || 'brand_action';
    setEditingCampaign(item.data);
    setSelectedCampaignMode(mode as CampaignMode);
    setShowForm(true);
  };

  // From the bell, or from an emailed ?campaign= link
  const openCampaignById = async (id: string, onInvoices = false) => {
    let found = campaigns.find(c => c.id === id) ?? null;
    if (!found) {
      try {
        found = await campaignService.getById(id);
      } catch (error) {
        log.error('Error loading campaign:', error);
      }
    }
    if (!found) {
      toast.error(t('campaigns.notFound'));
      return;
    }
    setFocusInvoices(onInvoices);
    setPageView('campaigns');
    handleEdit({ kind: 'campaign', data: found });
  };

  const openProjectoById = async (id: string, onInvoices = false) => {
    let found = projectoCampaigns.find(p => p.id === id) ?? null;
    if (!found) {
      try {
        found = await projectoCampaignService.getById(id);
      } catch (error) {
        log.error('Error loading project:', error);
      }
    }
    if (!found) {
      toast.error(t('campaigns.notFound'));
      return;
    }
    setFocusInvoices(onInvoices);
    setPageView('campaigns');
    setKpiModalOpen(false);
    handleEdit({ kind: 'projecto', data: found });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('campaign');
    const linkedProjecto = params.get('projecto');
    if ((!linked && !linkedProjecto) || loading) return;
    params.delete('campaign');
    params.delete('projecto');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    if (linked) openCampaignById(linked, true);
    else if (linkedProjecto) openProjectoById(linkedProjecto, true);
  }, [loading]);

  const handleDelete = async (id: string) => {
    try {
      const item = filteredItems.find(i => i.data.id === id);
      if (item?.kind === 'projecto') {
        await projectoCampaignService.delete(id);
      } else {
        await campaignService.delete(id);
      }
      await loadCampaigns();
    } catch (error) {
      log.error('Error deleting campaign:', error);
      toast.error(error instanceof Error && error.message === CAMPAIGN_DELETE_DENIED
        ? t('campaigns.deleteDenied')
        : t('campaigns.deleteFailed'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
    setEditingProjecto(null);
    setSelectedCampaignMode(null);
    setFocusInvoices(false);
  };

  // Forms stay open after saving; refresh the list and, for a campaign that
  // was just created, hand the record back so the form is now editing it
  const handleCampaignSaved = (saved?: CampaignWithComputed) => {
    loadCampaigns();
    loadKpiStats(filters.year);
    if (saved) setEditingCampaign(saved);
  };

  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showForm]);


  const handleKpiClick = (kpiType: KpiType) => {
    const today = new Date();
    const yearFiltered = filters.year
      ? campaigns.filter(c => c.year === filters.year)
      : campaigns;
    const requestedIds = new Set(kpiStats.invoice_request_campaign_ids ?? []);
    let kpiFiltered: CampaignWithComputed[] = [];
    let title = '';

    switch (kpiType) {
      case 'campaigns':
        title = t('kpiModal.runningCampaigns');
        kpiFiltered = yearFiltered.filter(c => {
          const status = c.campaign_status || 'Consultation';
          return status === 'In Progress' || status === 'Billing' || status === 'Consultation';
        });
        break;
      case 'invoices_not_issued':
        title = t('kpiModal.invoicesNotIssued');
        // Same rule as the card count, so the modal lists exactly what it counts
        kpiFiltered = yearFiltered.filter(c => needsClientInvoice(c) && !requestedIds.has(c.id));
        break;
      case 'invoice_requests':
        // Requests are a to-do list: every year
        title = t('kpiModal.invoiceRequests');
        kpiFiltered = campaigns.filter(c => requestedIds.has(c.id));
        break;
      case 'invoices_overdue':
        title = t('kpiModal.invoicesOverdue');
        kpiFiltered = yearFiltered.filter(c => hasOverdueInvoices(c));
        break;
      case 'payments_ready':
        title = t('kpiModal.paymentsReady');
        kpiFiltered = yearFiltered.filter(isPaymentReady);
        break;
    }

    setKpiModalTitle(title);
    setKpiModalProjectos(kpiType === 'invoice_requests' ? kpiStats.invoice_request_projectos ?? [] : []);
    setKpiModalCampaigns(kpiFiltered);
    setKpiModalOpen(true);
  };

  const handleKpiMarkAsPaid = async (campaignId: string, amount: number, creatorName?: string) => {
    await campaignService.markInfluencerAsPaid(campaignId, amount, creatorName);
    toast.success(t('payments.recordedSuccess'));
    const [freshCampaigns] = await Promise.all([
      campaignService.getAll(),
      loadKpiStats(filters.year),
    ]);
    setCampaigns(freshCampaigns);
    const yearScoped = filters.year
      ? freshCampaigns.filter(c => c.year === filters.year)
      : freshCampaigns;
    setKpiModalCampaigns(yearScoped.filter(isPaymentReady));
  };

  const currentYear = new Date().getFullYear();
  const uniqueYears = Array.from(new Set([currentYear, ...campaigns.map((c) => c.year)])).sort((a, b) => b - a);
  const uniqueBrands = Array.from(new Set(campaigns.map((c) => c.brand).filter(Boolean))).sort();
  const uniqueInfluencers = Array.from(new Set(campaigns.map((c) => c.influencer).filter(Boolean))).sort();
  const uniqueAgents = Array.from(
    new Set(campaigns.map((c) => c.agent_name).filter(Boolean))
  ).sort();

  const handleCampaignEdit = (c: CampaignWithComputed) => handleEdit({ kind: 'campaign', data: c });
  const filteredCampaignsForView = filteredItems
    .filter((i): i is { kind: 'campaign'; data: CampaignWithComputed } => i.kind === 'campaign')
    .map(i => i.data);

  return (
    <div className="min-h-screen bg-brand-paper">
      <div className="max-w-[1920px] mx-auto p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 gap-4">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="bg-white rounded-2xl p-2 shadow-card">
                <img src="/logo_mohit_bw.png" alt="MOHIT Logo" className="h-8 md:h-10 w-auto" />
              </div>
              <div>
                <h1 className="mo-display text-xl md:text-3xl text-brand-ink">{t('app.title')}</h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 hidden sm:block">
                  {t('app.subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 justify-end flex-shrink-0 pr-48 md:pr-52">
              <HubSwitcher current="main" className="hidden sm:block" />
              <button
                onClick={() => setMobileNavOpen(true)}
                className="sm:hidden flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-card"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </button>
              <NotificationBell
                onOpenCampaign={id => openCampaignById(id, true)}
                onOpenProjecto={id => openProjectoById(id, true)}
              />
              <div id="profile-menu-slot" data-testid="profile-menu-slot" className="relative z-40 rounded-full bg-white shadow-card">
                <ProfileMenu />
              </div>
              <button
                onClick={() => {
                  loadCampaigns();
                  loadKpiStats(filters.year);
                }}
                disabled={loading || kpiLoading}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-full shadow-sm ring-1 ring-gray-100 hover:ring-gray-200 hover:shadow transition-all disabled:opacity-50 touch-target text-sm font-medium"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${loading || kpiLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{t('campaigns.refresh')}</span>
              </button>
            </div>
          </div>
        </div>

        {!showForm && (
          <div className="fixed top-6 right-6 md:top-8 md:right-8 z-50">
            <CampaignModeSelector
              buttonLabel={t('campaigns.new')}
              onSelect={(mode) => {
                setSelectedCampaignMode(mode);
                setShowForm(true);
              }}
            />
          </div>
        )}

        {/* Mobile nav drawer */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-[60] sm:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl flex flex-col animate-slide-in-left">
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <img src="/logo_mohit_bw.png" alt="MOHIT" className="h-8 w-auto" />
                <button onClick={() => setMobileNavOpen(false)} className="p-2 rounded-full hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {NAV_ITEMS.filter(item => item.show).map(item => (
                  <button
                    key={item.key}
                    onClick={() => { setPageView(item.key); setMobileNavOpen(false); }}
                    className={`pressable w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      pageView === item.key ? 'bg-brand-ink text-brand-lime' : 'text-gray-700 hover:bg-brand-tint'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
              <div className="p-3 border-t border-gray-100">
                <button
                  onClick={() => { loadCampaigns(); loadKpiStats(filters.year); setMobileNavOpen(false); }}
                  disabled={loading || kpiLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 text-gray-700 rounded-xl text-sm font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading || kpiLoading ? 'animate-spin' : ''}`} />
                  <span>{t('campaigns.refresh')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-brand-ink rounded-full shadow-float mb-4 md:mb-6 hidden sm:inline-flex max-w-full">
          <div className="flex items-center gap-1 p-2 overflow-x-auto scrollbar-thin">
            {NAV_ITEMS.filter(item => item.show).map(item => (
              <button
                key={item.key}
                onClick={() => setPageView(item.key)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap touch-target text-sm font-medium transition-colors duration-200 ${
                  pageView === item.key ? 'text-brand-ink' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                {pageView === item.key && (
                  <motion.span
                    layoutId="main-nav-pill"
                    className="absolute inset-0 rounded-full bg-brand-lime"
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                  />
                )}
                <item.icon className="relative z-10 w-4 h-4" />
                <span className="relative z-10">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={pageView}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
        >
        {pageView === 'campaigns' && (
          <>
            <Dashboard kpiStats={kpiStats} loading={kpiLoading} onKpiClick={handleKpiClick} />

            <FilterToolbar
              campaignModeFilter={campaignModeFilter}
              onCampaignModeChange={setCampaignModeFilter}
              selectedStatuses={selectedStatuses}
              onStatusChange={setSelectedStatuses}
              statusCounts={statusCounts}
              filters={filters}
              onFilterChange={setFilters}
              totalCampaigns={campaigns.length}
              filteredCount={filteredCampaignsForView.length}
              uniqueYears={uniqueYears}
              uniqueBrands={uniqueBrands}
              uniqueInfluencers={uniqueInfluencers}
              uniqueAgents={uniqueAgents}
              modeCounts={modeCounts}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              showViewToggle={campaignModeFilter !== 'influencer_marketing' && campaignModeFilter !== 'projecto'}
              canViewOthersCampaigns={userRole?.can_view_others_campaigns || userRole?.can_manage_users || false}
            />

            <FilterChips
              selectedStatuses={selectedStatuses}
              onStatusRemove={(status) => setSelectedStatuses(prev => prev.filter(s => s !== status))}
              onClearStatuses={() => setSelectedStatuses([])}
              filters={filters}
              onFilterRemove={(key) => {
                const newFilters = { ...filters };
                delete newFilters[key];
                setFilters(newFilters);
              }}
              onClearAll={() => setFilters({})}
            />

            <motion.div
              key={`${campaignModeFilter}-${viewMode}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
            >
            {loading ? (
              <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
                <RefreshCw className="w-12 h-12 mx-auto mb-4 text-green-500 animate-spin" />
                <p className="text-gray-500 text-lg">{t('campaigns.loadingCampaigns')}</p>
              </div>
            ) : campaignModeFilter === 'projecto' ? (
              <ProjectoList
                externalProjects={projectoCampaigns}
                onEdit={(proj) => {
                  setEditingProjecto(proj);
                  setSelectedCampaignMode('projecto');
                  setShowForm(true);
                }}
                onRefresh={loadCampaigns}
                searchFilter={filters.search}
              />
            ) : campaignModeFilter === 'influencer_marketing' ? (
              <MultiInfluencerTableView campaigns={filteredCampaignsForView} onEdit={handleCampaignEdit} onDelete={handleDelete} userRole={userRole} currentUserId={user?.id} />
            ) : viewMode === 'grid' ? (
              <CampaignGridView campaigns={filteredCampaignsForView} onEdit={handleCampaignEdit} onDelete={handleDelete} userRole={userRole} currentUserId={user?.id} />
            ) : (
              <TableView campaigns={filteredCampaignsForView} onEdit={handleCampaignEdit} onDelete={handleDelete} userRole={userRole} campaignModeFilter={campaignModeFilter} currentUserId={user?.id} />
            )}
            </motion.div>
          </>
        )}

        {pageView === 'clients' && (userRole?.can_view_clients || userRole?.can_manage_users) && <ClientsManager />}

        {pageView === 'influencers' && (userRole?.can_view_influencers || userRole?.can_manage_users) && <InfluencersManager />}

        {pageView === 'services' && <ServicesManager />}

        {pageView === 'payments' && (userRole?.can_view_payments || userRole?.can_manage_users) && <PaymentsPage onCampaignClick={handleCampaignEdit} />}

        {pageView === 'commissions' && (userRole?.can_view_commissions || userRole?.can_manage_users) && <Commissions />}

        {pageView === 'statistics' && (userRole?.can_view_statistics || userRole?.can_manage_users) && <Statistics />}

        {pageView === 'admin' && <AdminPanel currentUserRole={userRole} />}
        </motion.div>
        </AnimatePresence>

        {showForm && selectedCampaignMode === 'brand_action' && (
          <CampaignForm campaign={editingCampaign} focusInvoices={focusInvoices} onClose={handleCloseForm} onSave={handleCampaignSaved} />
        )}

        {showForm && selectedCampaignMode === 'influencer_marketing' && (
          <CampaignFormMultiInfluencer campaign={editingCampaign} focusInvoices={focusInvoices} onClose={handleCloseForm} onSave={handleCampaignSaved} />
        )}

        {showForm && selectedCampaignMode === 'sponsorship' && (
          <CampaignFormSponsorship campaign={editingCampaign} focusInvoices={focusInvoices} onClose={handleCloseForm} onSave={handleCampaignSaved} />
        )}

        {showForm && selectedCampaignMode === 'projecto' && (
          <ProjectoForm project={editingProjecto} initialTab={focusInvoices ? 'invoices' : undefined} onClose={handleCloseForm} onSave={loadCampaigns} />
        )}

        {showForm && selectedCampaignMode === 'services' && (
          <CampaignForm campaign={editingCampaign} mode="services" focusInvoices={focusInvoices} onClose={handleCloseForm} onSave={handleCampaignSaved} />
        )}

        <KpiModal
          isOpen={kpiModalOpen}
          onClose={() => setKpiModalOpen(false)}
          title={kpiModalTitle}
          campaigns={kpiModalCampaigns}
          projectos={kpiModalProjectos}
          onOpenProjecto={id => openProjectoById(id, true)}
          onEdit={handleCampaignEdit}
          onMarkAsPaid={handleKpiMarkAsPaid}
        />
      </div>
    </div>
  );
}
